import React, { useState, useMemo, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, X, FileText, FilterX } from "lucide-react";
import { Button } from "@shared/ui/button";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Badge } from "@shared/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import {
  useCreateTemplateOperations,
  useDownloadTemplateOperations,
  useManualRunStatusChoices,
  useMeterRun,
  useMeterRunExport,
  useMeterRunSummary,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { PlayCircle, Clock, CheckCircle, AlertTriangle } from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { logEvent } from "@shared/analytics/analytics";
import { useToast } from "@shared/hooks/use-toast";
import { toast } from "@shared/ui/use-toast";

// Type definition for MeterRun data based on API response
interface MeterRun {
  id: number;
  meterSchedule: {
    id: number;
    meterCycle: {
      id: number;
      meterCycleName: string;
      meterType: string;
      productCode: string[];
    };
  };
  status: string;
  startDate: string;
  endDate: string;
  initialConsumer: any;
  premiseCount: number;
  isActive: boolean;
  summary: {
    meterCount: number;
    consumerCount: number;
    readingCount: number;
    rcntCount: number;
    faultyCount: number;
    normalCount: number;
  };
  createdDate: string;
  month: string;
  dispatchStatus: string;
  lastRunDate: string;
  nextRunDate: string;
  scheduleFrequency: string;
  completionPercentage: number;
}

interface MeterRunResponse {
  page_number: number;
  count: number;
  next: string | null;
  previous: string | null;
  results: MeterRun[];
}

interface MeterRunSummaryResponse {
  result: {
    totalRuns: number;
    notStartedRuns: number;
    inProgressRuns: number;
    completedRuns: number;
    delayedRuns: number;
  };
}

interface ManualMeterFilters {
  month: string;
  status: string;
}

const ManualMeterReads: React.FC = () => {
  // EXISTING LOGIC - keep your current state
  const [page, setPage] = useState(1);
  const [selectedCycle, setSelectedCycle] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const remoteUtilityId = getRemoteUtilityId();

  // NEW FILTER LOGIC - URL-based filtering
  const [searchParams, setSearchParams] = useSearchParams();

  // Read from URL parameters
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const monthFilter = searchParams.get("month") || "";
  const statusFilter = searchParams.get("status") || "";
    const { toast } = useToast();
 

  // Pending filters: UI state for user edits
  const [pendingFilters, setPendingFilters] = useState<ManualMeterFilters>({
    month: decodeURIComponent(monthFilter),
    status: decodeURIComponent(statusFilter),
  });

  // Sync pendingFilters when URL params change
  useEffect(() => {
    setPendingFilters({
      month: decodeURIComponent(monthFilter),
      status: decodeURIComponent(statusFilter),
    });
  }, [monthFilter, statusFilter]);

  const handlePendingFilterChange = (
    key: keyof ManualMeterFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // NEW FILTER HANDLERS
  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.month && { month: pendingFilters.month }),
      ...(pendingFilters.status && { status: pendingFilters.status }),
    };

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.month;
    delete newParams.status;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(monthFilter && { month: monthFilter }),
      ...(statusFilter && { status: statusFilter }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }

    setSearchParams(newParams);
  };

  const pageSize = 10;
  const meterType = "MANUAL_METER";

  // EXISTING API HOOKS - keep unchanged
  const { data: meterRunData, isLoading: isLoadingRuns } = useMeterRun({
    remote_utility_id: remoteUtilityId,
    meter_type: meterType,
    page,
    page_size: pageSize,
  });

  const { data: summaryData, isLoading: isLoadingSummary } = useMeterRunSummary(
    {
      remote_utility_id: remoteUtilityId,
      meter_type: meterType,
    }
  );

  const { exportCSV, isExporting } = useMeterRunExport({
    remote_utility_id: remoteUtilityId,
    meter_type: meterType,
    run_status: statusFilter || undefined,
    month_year: monthFilter || undefined,
  });

  const handleExport = () => {
    exportCSV();
  };
  const { data: manualStatusChoicesData, isLoading: isLoadingManualStatusChoices } =
    useManualRunStatusChoices();
  const downloadTemplateOperations = useDownloadTemplateOperations();
  const createTemplateOperations = useCreateTemplateOperations();

  // NEW FILTERING LOGIC - applied on top of your existing data
  const filteredData = useMemo(() => {
    if (!meterRunData?.results) return [];

    let filtered = meterRunData.results;

    // Apply search filter
    if (searchQuery.trim()) {
      filtered = filtered.filter((item) =>
        item.meterSchedule.meterCycle.meterCycleName
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    }

    // Apply month filter
    if (monthFilter) {
      filtered = filtered.filter((item) => {
        const itemMonth = item.month.toLowerCase();
        const filterMonth = monthFilter.toLowerCase();

        // Handle different month formats
        const monthMappings = {
          january: ["jan", "january"],
          february: ["feb", "february"],
          march: ["mar", "march"],
          april: ["apr", "april"],
          may: ["may"],
          june: ["jun", "june"],
          july: ["jul", "july"],
          august: ["aug", "august"],
          september: ["sep", "september", "sept"],
          october: ["oct", "october"],
          november: ["nov", "november"],
          december: ["dec", "december"],
        };

        const possibleMatches = monthMappings[filterMonth] || [filterMonth];
        return possibleMatches.some((match) => itemMonth.includes(match));
      });
    }

    // Apply status filter
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter((item) =>
        item.dispatchStatus.toLowerCase().includes(statusFilter.toLowerCase())
      );
    }

    return filtered;
  }, [meterRunData?.results, searchQuery, monthFilter, statusFilter]);

  // EXISTING HELPER FUNCTIONS - keep unchanged
  const formatDate = (dateStr: string | null) => {
    if (!dateStr || dateStr === "Not scheduled") return "Not scheduled";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const ProgressBar = ({ completion }: { completion: number }) => {
    const getBarColor = (percent: number) => {
      if (percent < 25) return "bg-red-500";
      if (percent < 50) return "bg-yellow-500";
      if (percent < 75) return "bg-blue-500";
      return "bg-green-500";
    };

    return (
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getBarColor(
              completion
            )}`}
            style={{ width: `${completion}%` }}
          />
        </div>
        <span className="text-sm font-medium">{completion}%</span>
      </div>
    );
  };

  const MeterQualityDisplay = ({
    summary,
  }: {
    summary: MeterRun["summary"];
  }) => {
    if (!summary) return <span>No data</span>;

    return (
      <div className="flex flex-wrap gap-1">
        <Badge
          variant="outline"
          className="bg-green-100 text-green-800 hover:bg-green-200 text-xs"
        >
          Normal: {summary.normalCount}
        </Badge>
        <Badge
          variant="outline"
          className="bg-red-100 text-red-800 hover:bg-red-200 text-xs"
        >
          Faulty: {summary.faultyCount}
        </Badge>
        <Badge
          variant="outline"
          className="bg-amber-100 text-amber-800 hover:bg-amber-200 text-xs"
        >
          RCNT: {summary.rcntCount}
        </Badge>
      </div>
    );
  };

  // EXISTING HANDLER FUNCTIONS - keep unchanged
  const handleDownloadExcel = (cycleId: string) => {
    downloadTemplateOperations.mutate({
      remote_utility_id: remoteUtilityId,
      run_id: cycleId,
    }, {
      onSuccess: () => {
toast({
  title: "Success",
  description: "File downloaded successfully",
  variant: "default",
});      }
    });
  };

  const handleOpenUpload = (cycleId: string) => {
    console.log('cycleId', cycleId);
    setSelectedCycle(cycleId);
    setIsUploadModalOpen(true);
    setSelectedFile(null);
  };

  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
    setSelectedCycle(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileUpload = () => {
    if (!selectedFile || !selectedCycle) {
       toast({
        title: "error",
        description: "No file selected or cycle ID missing",
      });
      
      return;
    }

    const payload: any = {
      file: selectedFile,
      remote_utility_id: remoteUtilityId,
    };

    createTemplateOperations.mutate(payload, {
      onSuccess: () => {
toast({
  title: "Success",
  description: "File uploaded successfully",
  variant: "default",
});
        handleCloseUploadModal();
      },
      onError: (error) => {
        console.error("Upload failed:", error);
      },
    });
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // EXISTING TABLE CONFIGURATION - keep unchanged
  const columns: TableColumn<MeterRun>[] = [
    {
      key: "meter_schedule.meter_cycle.meter_cycle_name",
      header: "Read Cycle",
      type: "custom",
      render: (value, row) => row.meterSchedule.meterCycle.meterCycleName,
      sortable: true,
    },
    {
      key: "month",
      header: "Month",
      type: "text",
      sortable: true,
    },
    {
      key: "summary.meter_count",
      header: "Meters",
      type: "custom",
      render: (value, row) => row.summary.meterCount,
      sortable: true,
    },
    {
      key: "summary.consumer_count",
      header: "Consumers",
      type: "custom",
      render: (value, row) => row.summary.consumerCount,
      sortable: true,
    },
    {
      key: "summary.reading_count",
      header: "Readings",
      type: "custom",
      render: (value, row) => row.summary.readingCount,
      sortable: true,
    },
    {
      key: "summary",
      header: "Meter Quality",
      type: "custom",
      render: (value, row) => <MeterQualityDisplay summary={row.summary} />,
    },
    {
      key: "completionPercentage",
      header: "Completion %",
      type: "custom",
      render: (value) => <ProgressBar completion={value} />,
      sortable: true,
    },
    {
      key: "dispatchStatus",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          const status = value;
          if (status?.includes("completed")) return "default";
          if (status?.includes("progress")) return "secondary";
          if (status?.includes("delayed")) return "destructive";
          if (status?.includes("not started")) return "outline";
          return "default";
        },
        getClassName: (value: string) => {
          const status = value;
          if (status?.includes("completed"))
            return "bg-green-100 text-green-800";
          if (status?.includes("progress")) return "bg-blue-100 text-blue-800";
          if (status?.includes("delayed")) return "bg-red-100 text-red-800";
          if (status?.includes("not started"))
            return "bg-gray-100 text-gray-800";
          return "";
        },
      },
      sortable: true,
    },
    {
      key: "lastRunDate",
      header: "Last Run Date",
      type: "custom",
      sortable: true,
    },
    {
      key: "nextRunDate",
      header: "Next Run Date",
      type: "custom",
      sortable: true,
    },
  ];

  const actions: TableAction<MeterRun>[] = [
    {
      label: "Upload Reading",
      onClick: (row) => {handleOpenUpload(row.id.toString()); logEvent('Manual Meter Reads Action Performed', {action: 'Upload Reading', cycleId: row.id})},
    },
    {
      label: "Download Template",
      onClick: (row) => {handleDownloadExcel(row.id.toString());  logEvent('Manual Meter Reads Action Performed', {action: 'Download Template', cycleId: row.id})},
    },
  ];

  // Status counts from summary API - KEEP YOUR EXISTING LOGIC
  const statusCounts = {
    notStarted: summaryData?.result?.notStartedRuns || 0,
    inProgress: summaryData?.result?.inProgressRuns || 0,
    completed: summaryData?.result?.completedRuns || 0,
    delayed: summaryData?.result?.delayedRuns || 0,
  };
  const statusKpiCards = [
    {
      title: "Not Started",
      count: statusCounts.notStarted,
      trendSummary: "Read cycles scheduled but not yet begun",
      trendDirection: "down" as const,
      icon: <PlayCircle className="h-8 w-8 text-gray-500" />,
    },
    {
      title: "In Progress",
      count: statusCounts.inProgress,
      trendSummary: "Read cycles currently being executed",
      trendDirection: "up" as const,
      icon: <Clock className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Completed",
      count: statusCounts.completed,
      trendSummary: "Read cycles successfully finished",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Delayed",
      count: statusCounts.delayed,
      trendSummary: "Read cycles behind schedule requiring attention",
      trendDirection: "down" as const,
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
    },
  ];
  // Calculate pagination info
  const totalItems = meterRunData?.count || 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  // NEW FILTER PANEL
  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Month</label>
          <Select
            value={pendingFilters.month}
            onValueChange={(value) => handlePendingFilterChange("month", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="january">January</SelectItem>
              <SelectItem value="february">February</SelectItem>
              <SelectItem value="march">March</SelectItem>
              <SelectItem value="april">April</SelectItem>
              <SelectItem value="may">May</SelectItem>
              <SelectItem value="june">June</SelectItem>
              <SelectItem value="july">July</SelectItem>
              <SelectItem value="august">August</SelectItem>
              <SelectItem value="september">September</SelectItem>
              <SelectItem value="october">October</SelectItem>
              <SelectItem value="november">November</SelectItem>
              <SelectItem value="december">December</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Status</label>
          <Select
            value={pendingFilters.status}
            onValueChange={(value) =>
              handlePendingFilterChange("status", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent>
              {manualStatusChoicesData?.result?.map((status) => (
                <SelectItem key={status.key} value={status.value.toLowerCase()}>
                  {status.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={resetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  // Check if filters are active
  const hasActiveFilters = monthFilter || statusFilter;

  const isLoading = isLoadingRuns || isLoadingSummary || isLoadingManualStatusChoices;

  return (
    <div className="w-full mx-auto px-4 space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="page-title">Manual Reads</h1>
          <p className="page-subtitle">
            Record and manage manual meter readings for cases where automated
            reads are unavailable.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-lg p-6 shadow-sm animate-pulse"
            >
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statusKpiCards.map((card, index) => (
            <QuickStatsCard
              key={index}
              title={card.title}
              count={card.count}
              trendSummary={card.trendSummary}
              trendDirection={card.trendDirection}
              icon={card.icon}
            />
          ))}
        </div>
      )}

      {/* File Upload Modal - KEEP YOUR EXISTING LOGIC */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Upload Reading File</DialogTitle>
            <DialogDescription>
              Select a file to upload readings for cycle ID: {selectedCycle}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-4">
              {/* File Input */}
              <div className="flex items-center justify-center w-full">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-4 text-gray-500" />
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      Excel files (.xlsx, .xls) or CSV files
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                  />
                </label>
              </div>

              {/* Selected File Display */}
              {selectedFile && (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-900">
                      {selectedFile.name}
                    </span>
                    <span className="text-xs text-blue-600">
                      ({(selectedFile.size / 1024).toFixed(1)} KB)
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveFile}
                    className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseUploadModal}>
              Cancel
            </Button>
            <Button onClick={handleFileUpload}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generic Table with Filters */}
      <GenericTable
        data={filteredData}
        columns={columns}
        rowKey="id"
        pagination={{
          currentPage: page,
          totalPages,
          totalItems,
          itemsPerPage: pageSize,
          startItem: (page - 1) * pageSize + 1,
          endItem: Math.min(page * pageSize, totalItems),
        }}
        onPageChange={setPage}
        onSearch={handleSearch}
        searchPlaceholder="Search by cycle name..."
        actions={actions}
        actionsType="dropdown"
        filterPanel={filterPanel}
        emptyMessage="No read cycles found matching your criteria"
        loading={isLoading}
        onExport={handleExport}
        exportLoading={isExporting}
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {[monthFilter, statusFilter].filter(Boolean).length} filter(s)
                active
              </span>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default ManualMeterReads;
